/**
 * Captures the origin of the host application (e.g. BIG) that embeds this chat
 * app in an iframe.
 *
 * The chat iframe is cross-origin to its host, so it cannot read
 * `window.parent.location`. However, every `postMessage` the host sends us
 * (the access/refresh-token handshake, report context, etc.) carries a
 * browser-set `event.origin` that IS the host's origin. We record it here so
 * features like thread-sharing can build a host-relative URL (a link that
 * opens inside the host rather than the bare chat-app origin, which is not
 * reachable for end users when the chat only lives embedded).
 *
 * The listener is installed once at module load (import this module early — it
 * is imported by AppWrapper) so it is active before we request tokens from the
 * parent and therefore catches the very first messages the host posts back.
 */
let parentOrigin: string | null = null;

if (typeof window !== 'undefined' && window.parent !== window) {
    window.addEventListener('message', (event: MessageEvent) => {
        // Only trust messages that actually come from our parent frame, carry a
        // real http(s) origin, and are not our own origin.
        if (
            event.source === window.parent &&
            event.origin &&
            event.origin !== window.location.origin &&
            /^https?:\/\//.test(event.origin)
        ) {
            parentOrigin = event.origin;
        }
    });
}

/** The embedding host's origin (e.g. "https://big.example.com"), or null if not embedded / not yet known. */
export function getParentOrigin(): string | null {
    return parentOrigin;
}
